import React from "react";

interface ScheduleSkeletonProps {
  isDarkMode?: boolean;
  columns?: number;
  rows?: number;
}

export default function ScheduleSkeleton({
  isDarkMode = false,
  columns = 6,
  rows = 13,
}: ScheduleSkeletonProps) {
  const bgMain = isDarkMode ? "bg-gray-900" : "bg-white";
  const borderCol = isDarkMode ? "border-gray-700" : "border-gray-200";
  const borderColSoft = isDarkMode ? "border-gray-600" : "border-gray-200";
  const headerBg = isDarkMode ? "bg-gray-700" : "bg-gray-50";
  const skeletonBg = isDarkMode ? "bg-gray-800" : "bg-gray-200";
  const skeletonBgSoft = isDarkMode ? "bg-gray-700" : "bg-gray-100";
  const gridTemplateColumns = `120px repeat(${columns}, minmax(150px, 1fr))`;

  return (
    <div className={`flex-auto flex flex-col h-screen isolate transition-colors duration-300 ${bgMain}`}>
      {/* Header */}
      <div
        className={`border-b p-6 transition-colors duration-300 ${borderCol}`}
      >
        <div className="flex items-center justify-between">
          {/* Left side - Filter and Add buttons */}
          <div className="flex items-center gap-2">
            <div className={`h-9 w-9 rounded ${skeletonBg}`} />
            <div className={`h-9 w-20 rounded ${skeletonBg}`} />
          </div>

          {/* Center - View title */}
          <div className={`h-6 w-64 rounded ${skeletonBg}`} />

          {/* Right side - View options */}
          <div className="flex items-center gap-2">
            <div className={`h-9 w-20 rounded ${skeletonBg}`} />
            <div className={`h-9 w-20 rounded ${skeletonBg}`} />
            <div className={`h-9 w-9 rounded ${skeletonBg}`} />
          </div>
        </div>
      </div>

      {/* Main calendar grid area - mirrors the week/day-by-practitioner table layout */}
      <div className="flex-1 flex flex-col max-h-screen overflow-hidden">
        <div className="flex-1 p-6 overflow-auto animate-pulse">
          <div
            className={`border rounded-lg overflow-hidden transition-colors duration-300 ${
              isDarkMode ? "bg-gray-800 border-gray-600" : "bg-white border-gray-200"
            }`}
          >
            {/* Column headers row (Time + practitioner/day columns) */}
            <div
              className={`grid border-b transition-colors duration-300 ${borderColSoft}`}
              style={{ gridTemplateColumns }}
            >
              <div className={`p-3 border-r transition-colors duration-300 ${headerBg} ${borderColSoft}`}>
                <div className={`h-3.5 w-10 rounded ${skeletonBgSoft}`} />
              </div>
              {Array.from({ length: columns }).map((_, i) => (
                <div
                  key={`col-header-${i}`}
                  className={`p-3 border-r transition-colors duration-300 ${headerBg} ${borderColSoft}`}
                >
                  <div className={`h-3.5 w-16 mx-auto rounded ${skeletonBgSoft}`} />
                  <div className={`h-2.5 w-8 mx-auto rounded mt-1.5 ${skeletonBgSoft}`} />
                </div>
              ))}
            </div>

            {/* Sub-header row - practitioner initials nested under each column */}
            <div
              className={`grid border-b transition-colors duration-300 ${
                isDarkMode ? "border-gray-600 bg-gray-800" : "border-gray-200 bg-gray-100"
              }`}
              style={{ gridTemplateColumns }}
            >
              <div className={`p-2 border-r transition-colors duration-300 ${borderColSoft}`} />
              {Array.from({ length: columns }).map((_, i) => (
                <div
                  key={`sub-header-${i}`}
                  className={`grid border-r transition-colors duration-300 ${borderColSoft}`}
                  style={{ gridTemplateColumns: "repeat(3, 1fr)" }}
                >
                  {Array.from({ length: 3 }).map((__, j) => (
                    <div
                      key={`sub-header-${i}-${j}`}
                      className="p-1.5 flex items-center justify-center"
                    >
                      <div className={`h-2 w-5 rounded ${skeletonBgSoft}`} />
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Time slot rows */}
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <div
                key={`row-${rowIndex}`}
                className={`grid border-b transition-colors duration-300 ${borderColSoft}`}
                style={{ gridTemplateColumns }}
              >
                <div className={`p-2 border-r transition-colors duration-300 ${headerBg} ${borderColSoft}`}>
                  <div className={`h-2.5 w-9 rounded ${skeletonBgSoft}`} />
                </div>
                {Array.from({ length: columns }).map((_, colIndex) => {
                  // Sprinkle occasional "appointment" placeholders instead of every cell
                  const showEvent = (rowIndex + colIndex) % 4 === 0;
                  // Vary the placeholder height to mimic different appointment durations
                  const heightVariants = ["h-8", "h-10", "h-12", "h-16", "h-20"];
                  const variantIndex =
                    (rowIndex * 3 + colIndex * 5) % heightVariants.length;
                  const eventHeight = heightVariants[variantIndex];
                  const showThirdLine = variantIndex >= 3;
                  return (
                    <div
                      key={`cell-${rowIndex}-${colIndex}`}
                      className={`min-h-10 p-1.5 border-r transition-colors duration-300 ${borderColSoft}`}
                    >
                      {showEvent && (
                        <div
                          className={`${eventHeight} rounded p-1.5 border ${
                            isDarkMode ? "bg-gray-700 border-gray-600" : "bg-gray-100 border-gray-200"
                          }`}
                        >
                          <div className={`h-2 w-3/4 rounded ${skeletonBg}`} />
                          <div className={`h-2 w-1/2 rounded mt-1 ${skeletonBg}`} />
                          {showThirdLine && (
                            <div className={`h-2 w-2/3 rounded mt-1 ${skeletonBg}`} />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

